import { call, put, select, takeLatest } from "redux-saga/effects";
import { editBranchAction } from "../../constants";
import { IBranch } from "../../types/shared";
import { selectToken } from "../selectors";
import * as actions from "../actions";
import { editBranch, IEditBranchData } from "../../axios/editBranch";
import { deleteOptimisticProps } from "./editStoreItemSaga";
import { delay } from "redux-saga/effects";
import { EVENTS_FOR_TRACKING } from "../../constants/events-for-tracking";
import { trackEvent } from "../../tracking/trackEvent";
import { IEditBranchAction } from "../../types/wizard-types";
import { editBranchZones } from "../../Components/Pickup/redux-store/actions/edit-branch-zones-action";
import { defaultRetry } from "../../Components/WizardComponents/Branches/AddBranchForm/redux/getSingleBranchSaga";
import { IRootReducerState } from "../rootReducer";
import { buildBranchScheduledServicesPayload } from "../../helpers/audit/utils";

function* editBranchSaga(action: IEditBranchAction) {
  const { payload } = action;
  try {
    const token = yield select(selectToken);
    // branchesReducer applies an optimistic update on requested before this saga
    // runs, so the pre-edit snapshot lives on fallBack — not on the branch itself.
    const branchInState: IBranch | undefined = yield select(
      (state: IRootReducerState) =>
        state.branchesReducer?.allBranches?.find(
          (b: IBranch) => b.id === payload.id
        )
    );
    const oldBranch: IBranch | undefined =
      (branchInState?.fallBack as IBranch | undefined) ?? branchInState;

    const branchData: IBranch | undefined = yield select(
      (state: IRootReducerState) => state.getSingleBranchReducer?.branchData
    );
    const serviceAudit = yield select((state: IRootReducerState) => state.serviceAuditReducer);
    const branchPayload = payload as typeof payload & Partial<IBranch>;

    const scheduled_services = buildBranchScheduledServicesPayload({
      branchId: payload.id,
      scheduledDisables: serviceAudit?.scheduledDisables || [],
      disableMetadata: serviceAudit?.disableMetadata || {},
      existingScheduledServices:
        branchPayload.scheduled_services ||
        (branchData?.id === payload.id ? branchData?.scheduled_services : undefined) ||
        oldBranch?.scheduled_services,
      branchStatuses: {
        pickup: branchPayload.pickup_status,
        delivery: branchPayload.delivery_status,
        drive_thru: branchPayload.drive_thru_status,
        offline_loyalty: branchPayload.offline_loyalty_status,
        online_loyalty: branchPayload.online_loyalty_status,
      },
    });

    const editBranchPayload: IEditBranchData = {
      ...deleteOptimisticProps(payload),
      scheduled_services,
    };

    const editedBranchRes = yield defaultRetry(
      editBranch,
      token,
      editBranchPayload
    );
    const branch = editedBranchRes.data as IBranch;
    yield put(actions.editBranchSuccess({ ...payload, ...branch }));
    trackEvent(EVENTS_FOR_TRACKING["Edited a cashier or a branch"]);

    if (
      editedBranchRes.status === 200 &&
      action.type === editBranchAction.requested
    ) {
      yield put(actions.getBranches());
    }
  } catch (e) {
    yield delay(1000);
    yield put(actions.editBranchFailure(payload));
  }
}

export function* watchEditBranchSaga() {
  yield takeLatest(
    [editBranchAction.requested, editBranchZones.type],
    editBranchSaga
  );
}
